import { supabase } from "@/integrations/supabase/client";
import { formatISO, addDays } from "date-fns";
import JSZip from "jszip";

interface FileWithPath extends File {
  path?: string;
}

function sanitizeFileName(fileName: string): string {
  const parts = fileName.split('.');
  const ext = parts.length > 1 ? parts.pop() : '';
  const name = parts.join('.');
  
  const sanitizedName = name
    .replace(/[^a-zA-Z0-9-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase()
    .substring(0, 32);
  
  const finalName = sanitizedName || 'file';
  const sanitizedExt = ext 
    ? ext.replace(/[^a-zA-Z0-9]/g, '').toLowerCase()
    : '';
  
  return sanitizedExt ? `${finalName}.${sanitizedExt}` : finalName;
}

function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

async function uploadInChunks(file: File, filePath: string, onProgress: (progress: number) => void) {
  const CHUNK_SIZE = 5 * 1024 * 1024;
  const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
  let uploadedChunks = 0;
  let uploadedBytes = 0;

  console.log('Iniciando subida en chunks:', {
    totalChunks,
    chunkSize: CHUNK_SIZE,
    totalSize: file.size,
    fileName: file.name
  });

  try {
    const { error } = await supabase.storage
      .from('transfers')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: true
      });

    if (error) throw error;

    onProgress(100);
    console.log('Archivo subido exitosamente:', filePath);
    return 1;
  } catch (error) {
    console.error('Error en la subida:', error);
    throw error;
  }
}

export async function uploadFile(file: FileWithPath, userId: string, onProgress: (progress: number) => void) {
  console.log('=== INICIO DE SUBIDA DE ARCHIVO ===');
  console.log('File:', { name: file.name, type: file.type, size: file.size });
  console.log('UserId:', userId);
  
  const MAX_FILE_SIZE = 50 * 1024 * 1024 * 1024; // 50GB
  
  if (file.size > MAX_FILE_SIZE) {
    throw new Error(`El archivo excede el límite de tamaño permitido (50GB)`);
  }
  
  try {
    onProgress(0);

    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || !session) {
      throw new Error('Error de autenticación');
    }

    const uniqueId = generateUUID();
    const sanitizedFileName = sanitizeFileName(file.name);
    const filePath = `${userId}/${uniqueId}-${sanitizedFileName}`;

    await uploadInChunks(file, filePath, onProgress);

    const { data: transferData, error: dbError } = await supabase
      .from('transfers')
      .insert({
        user_id: userId,
        filename: filePath,
        original_name: file.name,
        content_type: file.type,
        size: file.size,
        expires_at: formatISO(addDays(new Date(), 7)),
      })
      .select()
      .single();

    if (dbError) {
      console.error('Error al crear registro en la base de datos:', dbError);
      throw dbError;
    }

    if (!transferData) {
      throw new Error('No se pudo crear el registro de transferencia');
    }

    onProgress(95);

    const { data: existingLink, error: linkError } = await supabase
      .from('shared_links')
      .insert({
        transfer_id: transferData.id,
        expires_at: formatISO(addDays(new Date(), 7)),
      })
      .select('id')
      .single();

    if (linkError) {
      console.error('Error al crear shared link:', linkError);
      throw new Error('No se pudo generar el enlace de descarga');
    }

    onProgress(100);

    return {
      ...transferData,
      linkId: existingLink.id,
    };
  } catch (error: any) {
    console.error('Error completo en uploadFile:', error);
    throw error;
  }
}

export async function uploadFolder(files: FileWithPath[], folderName: string, userId: string, onProgress: (progress: number) => void) {
  console.log('Iniciando proceso de carpeta:', {
    filesCount: files.length,
    folderName,
    userId
  });
  
  const zip = new JSZip();
  const MAX_ZIP_SIZE = 50 * 1024 * 1024 * 1024; // 50GB en bytes
  let totalSize = 0;
  
  files.forEach((file) => {
    totalSize += file.size;
  });
  
  if (totalSize > MAX_ZIP_SIZE) {
    throw new Error(`El tamaño total de los archivos (${(totalSize / 1024 / 1024 / 1024).toFixed(2)}GB) excede el límite de 50GB`);
  }
  
  files.forEach((file) => {
    const relativePath = file.path || file.name;
    zip.file(relativePath, file);
  });

  try {
    console.log('Comprimiendo archivos...');
    const zipBlob = await zip.generateAsync({ 
      type: "blob",
      compression: "DEFLATE",
      compressionOptions: { level: 5 }
    }, (metadata) => {
      onProgress(metadata.percent / 2);
    });

    if (zipBlob.size > MAX_ZIP_SIZE) {
      throw new Error(`El archivo ZIP resultante (${(zipBlob.size / 1024 / 1024 / 1024).toFixed(2)}GB) excede el límite de 50GB`);
    }

    const sanitizedFolderName = sanitizeFileName(folderName);
    const zipFile = new File([zipBlob], `${sanitizedFolderName}.zip`, { type: "application/zip" });
    
    const uploadProgress = (progress: number) => {
      onProgress(50 + (progress / 2));
    };
    
    return uploadFile(zipFile, userId, uploadProgress);
  } catch (error: any) {
    console.error('Error en compresión:', error);
    throw error;
  }
}

export async function generateShareLink(transferId: string) {
  console.log('Generando link para transferId:', transferId);
  
  try {
    const { data: existingLink, error: fetchError } = await supabase
      .from('shared_links')
      .select('id')
      .eq('transfer_id', transferId)
      .single();

    if (fetchError && fetchError.code !== 'PGRST116') {
      console.error('Error al buscar link existente:', fetchError);
      throw fetchError;
    }

    if (existingLink) {
      console.log('Link existente encontrado:', existingLink.id);
      return existingLink.id;
    }

    const { data: newLink, error: createError } = await supabase
      .from('shared_links')
      .insert({
        transfer_id: transferId,
        expires_at: formatISO(addDays(new Date(), 7)),
      })
      .select('id')
      .single();

    if (createError) {
      console.error('Error al crear nuevo link:', createError);
      throw createError;
    }
    
    if (!newLink) {
      throw new Error("No se pudo crear el enlace compartido");
    }

    console.log('Nuevo link creado:', newLink.id);
    return newLink.id;
  } catch (error) {
    console.error("Error generando enlace compartido:", error);
    throw new Error("No se pudo generar el enlace de descarga");
  }
}
